import React, { useCallback, useEffect, useState } from 'react';
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiCreditCard,
  FiPackage,
  FiRefreshCw,
  FiSearch,
  FiSend,
  FiTrash2,
  FiUser,
  FiX,
} from 'react-icons/fi';
import MainLayout from '../components/layout/MainLayout.jsx';
import { Alert, Button, DeleteConfirm, PageHeader } from '../components/common/index.js';
import { paymentsApi } from '../api/paymentsApi';
// The counter book ends at this same checkpoint — see CounterCheckpoint.
import CounterCheckpoint from '../components/ops/CounterCheckpoint.jsx';
import { counterApi } from '../api/opsApi';
import { PAYMENT_MODE_OPTIONS } from '../constants/paymentModes';

const PAYMENT_MODES = PAYMENT_MODE_OPTIONS;

const FILTERS = [
  { value: 'needs_action', label: 'Awaiting confirmation' },
  { value: 'pending', label: 'Pending' },
  { value: 'to_confirm', label: 'To confirm' },
  { value: 'balance_due', label: 'Balance due' },
  { value: 'recorded', label: 'Recorded' },
  { value: 'all', label: 'All' },
];

const formatPrice = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const shortDate = (value) => (value ? String(value).split(' at ')[0] : '-');

const errorMessage = (error, fallback) => {
  const data = error?.response?.data ?? error;
  if (!data) return fallback;
  if (typeof data === 'string') return data;
  return data.detail || data.message || data.error || fallback;
};

const Modal = ({ title, subtitle, children, onClose }) => {
  useEffect(() => {
    const onEscape = (event) => event.key === 'Escape' && onClose();
    document.addEventListener('keydown', onEscape);
    return () => document.removeEventListener('keydown', onEscape);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
      <button className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} aria-label="Close dialog" />
      <div className="relative w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl animate-fade-in-up">
        <div className="flex items-start justify-between border-b border-slate-100 px-5 py-4">
          <div className="min-w-0">
            <h2 className="text-base font-bold text-slate-900">{title}</h2>
            {subtitle && <p className="mt-0.5 truncate text-xs text-slate-500">{subtitle}</p>}
          </div>
          <button onClick={onClose} className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700" aria-label="Close">
            <FiX className="h-4 w-4" />
          </button>
        </div>
        <div className="max-h-[70vh] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
};

// What was actually sold on this deal — parts with qty, price and CAP/OSM.
const SoldParts = ({ parts, compact = false }) => {
  if (!parts || parts.length === 0) {
    return <p className="text-xs italic text-slate-400">No itemized parts on this sale.</p>;
  }
  return (
    <ul className={compact ? 'space-y-1' : 'space-y-1.5'}>
      {parts.map((p, i) => (
        <li key={i} className="flex items-center justify-between gap-2 text-sm">
          <span className="flex min-w-0 items-center gap-1.5 text-slate-700">
            <FiPackage className="h-3.5 w-3.5 shrink-0 text-slate-300" />
            <span className="truncate font-semibold">{p.name}</span>
            {p.quantity > 1 && <span className="shrink-0 text-xs text-slate-400">×{p.quantity}</span>}
            {p.source && (
              <span
                className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                  p.source === 'cap' ? 'bg-violet-50 text-violet-600' : 'bg-amber-50 text-amber-600'
                }`}
              >
                {p.source}
              </span>
            )}
          </span>
          <span className="shrink-0 text-sm font-bold text-slate-800">{formatPrice(p.price) || '—'}</span>
        </li>
      ))}
    </ul>
  );
};

const Spinner = () => (
  <div className="flex items-center justify-center py-16">
    <svg className="h-8 w-8 animate-spin text-violet-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  </div>
);

const StatCard = ({ icon: Icon, label, value, tone = 'violet' }) => {
  const tones = {
    violet: 'bg-violet-50 text-violet-600',
    amber: 'bg-amber-50 text-amber-600',
    emerald: 'bg-emerald-50 text-emerald-600',
    sky: 'bg-sky-50 text-sky-600',
    rose: 'bg-rose-50 text-rose-600',
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tones[tone]}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</p>
          <p className="mt-0.5 text-2xl font-bold leading-none text-slate-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

// ── Collect-balance modal ─────────────────────────────────────────────────────
// An advance was taken; the customer still owes the rest. Admin marks it
// collected here (optionally noting how the balance was paid).
const CollectBalanceModal = ({ row, submitting, error, onClose, onSubmit }) => {
  const p = row.payment || {};
  const [mode, setMode] = useState('');
  return (
    <Modal
      title="Collect balance"
      subtitle={`${row.customer_name || 'Customer'} · ${row.car || ''}`}
      onClose={onClose}
    >
      <div className="space-y-4">
        {error && <Alert type="error" message={error} />}

        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3">
          <div className="flex items-baseline justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-amber-600">Balance to collect</p>
            <p className="text-2xl font-black text-slate-900">{formatPrice(p.balance_due) || '—'}</p>
          </div>
          <p className="mt-1 text-xs text-slate-500">
            Total {formatPrice(p.total) || '—'} · advance {formatPrice(p.advance_amount) || '—'} ({p.mode_label || '-'})
          </p>
        </div>

        <div>
          <p className="mb-2 block text-sm font-semibold text-slate-700">How was the balance paid? <span className="font-normal text-slate-400">(optional)</span></p>
          <div className="grid grid-cols-4 gap-2">
            {PAYMENT_MODES.map((m) => {
              const active = mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMode(active ? '' : m.value)}
                  className={`rounded-lg border px-2 py-2 text-xs font-bold transition-all ${active ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600 hover:border-emerald-300'}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="button" variant="primary" loading={submitting} onClick={() => onSubmit(mode)} className="gap-2">
            <FiCheckCircle className="h-4 w-4" /> Balance received
          </Button>
        </div>
      </div>
    </Modal>
  );
};

// ── Set-payment modal ─────────────────────────────────────────────────────────
const SetPaymentModal = ({ row, submitting, error, onClose, onSubmit }) => {
  const existing = row.payment;
  // sales reported a payment that admin hasn't confirmed → this is a confirm
  const isConfirm = existing && !existing.confirmed;
  // `total` = LIVE deal after cancellations (net). The advance validates against
  // the ORIGINAL quote — one collected before a cancellation may legitimately
  // exceed the reduced deal; the excess is a refund owed, not an input error.
  const total = row.total != null ? Number(row.total) : null;
  const gross = row.gross_total != null ? Number(row.gross_total) : total;
  const cancelledTotal = row.cancelled_total != null ? Number(row.cancelled_total) : 0;
  const [mode, setMode] = useState(existing?.mode || '');
  // 'final' is not offered on deals with cancelled parts (double-count guard) —
  // coerce a legacy 'final' record to 'full' so the form isn't stuck on a
  // hidden option the backend would reject.
  const [type, setType] = useState(
    existing?.type === 'final' && Number(row.cancelled_total) > 0 ? 'full' : (existing?.type || 'full')
  );
  const [advance, setAdvance] = useState(existing?.advance_amount ? String(Number(existing.advance_amount)) : '');
  const [finalAmount, setFinalAmount] = useState(existing?.final_amount ? String(Number(existing.final_amount)) : '');
  const [formError, setFormError] = useState('');

  const advanceNum = Number(advance);
  const advanceValid = Number.isFinite(advanceNum) && advanceNum > 0 && (gross == null || advanceNum <= gross);
  const balance = total != null && advanceValid ? total - advanceNum : null;
  // Advance over-pays the shrunken deal → company owes the customer this back
  const refund = balance != null && balance < 0 ? -balance : 0;

  const finalNum = Number(finalAmount);
  const finalValid = Number.isFinite(finalNum) && finalNum > 0;
  // difference vs the quoted total, shown so admin sees what was given/added
  const finalDiff = total != null && finalValid ? finalNum - total : null;

  const submit = (event) => {
    event.preventDefault();
    setFormError('');
    if (!mode) { setFormError('Select the payment mode'); return; }
    const payload = { payment_mode: mode, payment_type: type };
    if (type === 'advance') {
      if (!advanceValid) {
        setFormError(gross != null && advanceNum > gross ? 'Advance cannot exceed the total' : 'Enter a valid advance amount');
        return;
      }
      payload.advance_amount = advanceNum;
    }
    if (type === 'final') {
      if (!finalValid) { setFormError('Enter the final amount collected'); return; }
      payload.final_amount = finalNum;
    }
    onSubmit(payload);
  };

  return (
    <Modal
      title={isConfirm ? 'Confirm payment' : existing ? 'Update payment' : 'Record payment'}
      subtitle={`${row.customer_name || 'Customer'} · ${row.car || ''}`}
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert type="error" message={error} />}

        {isConfirm && (
          <div className="rounded-xl border border-sky-100 bg-sky-50 px-4 py-2.5 text-xs text-sky-800">
            <span className="font-bold">Sales reported this payment.</span> Check it's right (or adjust), then confirm.
          </div>
        )}

        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
          <div className="flex items-baseline justify-between">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Order total</p>
            <p className="text-xl font-bold text-slate-900">
              {cancelledTotal > 0 && gross != null && gross !== total && (
                <span className="mr-2 text-sm font-semibold text-slate-300 line-through">{formatPrice(gross)}</span>
              )}
              {total != null ? formatPrice(total) : '—'}
            </p>
          </div>
          {cancelledTotal > 0 && (
            <p className="mt-0.5 text-right text-[11px] font-semibold text-rose-500">
              ✕ {formatPrice(cancelledTotal)} cancelled — deal reduced
            </p>
          )}
          <div className="mt-2 border-t border-slate-200/70 pt-2">
            <SoldParts parts={row.parts} />
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Sold by {row.salesperson_name || '-'} · won {shortDate(row.won_at)}
          </p>
        </div>

        <div>
          <p className="mb-2 block text-sm font-semibold text-slate-700">Payment mode *</p>
          <div className="grid grid-cols-4 gap-2">
            {PAYMENT_MODES.map((m) => {
              const active = mode === m.value;
              return (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMode(m.value)}
                  className={`rounded-lg border px-2 py-2 text-xs font-bold transition-all ${active ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
                >
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <p className="mb-2 block text-sm font-semibold text-slate-700">Payment type *</p>
          <div className={`grid gap-3 ${cancelledTotal > 0 ? 'grid-cols-2' : 'grid-cols-3'}`}>
            {[{ value: 'full', label: 'Full payment' }, { value: 'advance', label: 'Advance' }, { value: 'final', label: 'Final payment' }]
              // A deal with cancelled/returned parts already nets via the refund
              // flow — a "final" amount would count that reduction twice, so the
              // backend rejects it. Don't offer the dead-end option.
              .filter((t) => t.value !== 'final' || cancelledTotal <= 0)
              .map((t) => {
              const active = type === t.value;
              return (
                <button
                  key={t.value}
                  type="button"
                  onClick={() => setType(t.value)}
                  className={`rounded-xl border-2 px-3 py-3 text-sm font-bold transition-all ${active ? 'border-violet-500 bg-violet-50 text-violet-700' : 'border-slate-200 bg-white text-slate-600 hover:border-violet-300'}`}
                >
                  {t.label}
                </button>
              );
            })}
          </div>
          {cancelledTotal > 0 && (
            <p className="mt-2 text-xs text-slate-500">
              Record what the customer <b>actually handed over</b> (full/advance). The ₹{formatPrice(cancelledTotal)} of
              cancelled parts is settled by the refund flow — don't reduce the payment for it here.
            </p>
          )}
          {type === 'final' && (
            <p className="mt-2 text-xs text-slate-500">Actual rate collected at the counter — becomes the deal's final total. Use it when the customer paid a settled amount different from the quote.</p>
          )}
        </div>

        {type === 'advance' && (
          <div>
            <label htmlFor="advance" className="mb-2 block text-sm font-semibold text-slate-700">Advance received *</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input
                id="advance"
                type="number"
                min="0.01"
                step="any"
                inputMode="numeric"
                value={advance}
                onChange={(e) => setAdvance(e.target.value)}
                disabled={submitting}
                placeholder={total != null ? `Total ${formatPrice(total)}` : 'Amount paid now'}
                className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-9 pr-4 text-sm font-semibold text-slate-800 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>
            {balance != null && refund > 0 ? (
              <p className="mt-1.5 text-xs font-bold text-rose-600">
                ↩ Refund {formatPrice(refund)} to customer — the advance exceeds the reduced order ({formatPrice(total)}). No balance to collect.
              </p>
            ) : balance != null ? (
              <p className="mt-1.5 text-xs font-semibold text-amber-600">Balance due: {formatPrice(balance)}</p>
            ) : null}
          </div>
        )}

        {type === 'final' && (
          <div>
            <label htmlFor="finalAmount" className="mb-2 block text-sm font-semibold text-slate-700">Final amount collected *</label>
            <div className="relative">
              <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">₹</span>
              <input
                id="finalAmount"
                type="number"
                min="0.01"
                step="any"
                inputMode="numeric"
                value={finalAmount}
                onChange={(e) => setFinalAmount(e.target.value)}
                disabled={submitting}
                placeholder={total != null ? `Quoted ${formatPrice(total)}` : 'Final rate received'}
                className="w-full rounded-xl border border-slate-200 bg-white py-3 pl-9 pr-4 text-sm font-semibold text-slate-800 placeholder-slate-400 transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
              />
            </div>
            {finalDiff != null && finalDiff !== 0 && (
              <p className={`mt-1.5 text-xs font-semibold ${finalDiff < 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                {finalDiff < 0
                  ? `${formatPrice(Math.abs(finalDiff))} less than quoted (${formatPrice(total)})`
                  : `${formatPrice(finalDiff)} more than quoted (${formatPrice(total)})`}
              </p>
            )}
          </div>
        )}

        {formError && (
          <p className="flex items-center gap-1.5 text-xs font-medium text-rose-500">
            <FiAlertCircle className="h-3.5 w-3.5" /> {formError}
          </p>
        )}

        <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
          <Button type="button" variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" variant="primary" loading={submitting} className="gap-2">
            <FiSend className="h-4 w-4" /> {isConfirm ? 'Confirm payment' : 'Save payment'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// ── Payment row card ──────────────────────────────────────────────────────────
const PaymentCard = ({ row, onSet, onCollect, onDelete }) => {
  const pending = row.payment_pending;
  const p = row.payment;
  // sales reported a payment, admin hasn't confirmed it yet
  const toConfirm = !pending && p && !p.confirmed;
  // advance taken, confirmed, remaining balance not collected — customer owes
  const balanceOpen = !pending && p.confirmed && p.type === 'advance' && !p.balance_paid && Number(p.balance_due) > 0;
  const highlight = pending || toConfirm || balanceOpen;
  return (
    <div className={`rounded-xl border bg-white p-4 shadow-sm ${highlight ? 'border-amber-200' : 'border-slate-200'}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-3">
          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl ${highlight ? 'bg-amber-50 text-amber-600' : 'bg-emerald-50 text-emerald-600'}`}>
            <FiUser className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="truncate text-base font-bold text-slate-900">{row.customer_name || 'Customer'}</h3>
            <p className="mt-0.5 text-sm text-slate-500">{row.car}</p>
            <p className="mt-0.5 text-xs font-medium text-slate-400">
              {Number(row.cancelled_total) > 0 && row.gross_total && (
                <span className="text-slate-300 line-through mr-1">{formatPrice(row.gross_total)}</span>
              )}
              <span className={Number(row.cancelled_total) > 0 ? 'font-bold text-slate-600' : ''}>{formatPrice(row.total) || '—'}</span>
              {' '}· by {row.salesperson_name || '-'} · won {shortDate(row.won_at)}
            </p>
            {Number(row.cancelled_total) > 0 && (
              <p className="mt-0.5 text-[11px] font-semibold text-rose-500">
                ✕ {formatPrice(row.cancelled_total)} cancelled — deal reduced to {formatPrice(row.total)}
              </p>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          {pending ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
              <FiClock className="h-3.5 w-3.5" /> Payment pending
            </span>
          ) : toConfirm ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-sky-50 px-2.5 py-1 text-xs font-bold text-sky-700">
              <FiClock className="h-3.5 w-3.5" /> Sales reported · confirm
            </span>
          ) : balanceOpen ? (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-700">
              <FiClock className="h-3.5 w-3.5" /> Advance · {formatPrice(p.balance_due)} due
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700">
              <FiCheckCircle className="h-3.5 w-3.5" />
              {p.type === 'advance'
                ? 'Paid in full (adv + balance)'
                : p.type === 'final'
                  ? `${p.mode_label} · Final`
                  : `${p.mode_label} · Full`}
            </span>
          )}
          <div className="flex items-center gap-1.5">
            {balanceOpen && (
              <Button size="sm" variant="primary" onClick={() => onCollect(row)} className="gap-1.5">
                <FiCheckCircle className="h-3.5 w-3.5" /> Collect balance
              </Button>
            )}
            <Button size="sm" variant={pending || toConfirm ? 'primary' : 'secondary'} onClick={() => onSet(row)} className="gap-1.5">
              <FiCreditCard className="h-3.5 w-3.5" />
              {pending ? 'Record payment' : toConfirm ? 'Confirm payment' : 'Edit'}
            </Button>
            {/* SUPERADMIN: wipe a wrong payment — only shows once one exists */}
            {!pending && (
              <button
                type="button"
                onClick={() => onDelete(row)}
                title="Delete this payment record (kept in Deleted Records)"
                className="rounded-lg border border-slate-200 p-2 text-slate-400 transition hover:border-rose-300 hover:bg-rose-50 hover:text-rose-600"
              >
                <FiTrash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sales-reported banner — what sales says they collected, awaiting admin OK */}
      {toConfirm && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-sky-100 bg-sky-50 px-3 py-2.5">
          <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-sky-500" />
          <div className="text-xs text-sky-800">
            <span className="font-bold">Sales reported: </span>
            {p.type === 'advance'
              ? <>Advance {formatPrice(p.advance_amount)} via {p.mode_label} · balance {formatPrice(p.balance_due)} still due</>
              : p.type === 'final'
                ? <>Final payment {formatPrice(p.final_amount)} via {p.mode_label}{p.quoted_total && Number(p.quoted_total) !== Number(p.final_amount) ? ` (quoted ${formatPrice(p.quoted_total)})` : ''}</>
                : <>Full payment {formatPrice(p.paid)} via {p.mode_label}</>}
            <span className="block mt-0.5 text-sky-600">Verify against your account, then confirm.</span>
          </div>
        </div>
      )}

      {/* What was sold — admin needs the parts, not just the car */}
      <div className="mt-3 rounded-lg border border-slate-100 bg-slate-50 px-3 py-2.5">
        <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-400">Parts sold</p>
        <SoldParts parts={row.parts} compact />
      </div>

      {/* Refund owed to the customer — an advance over-paid after cancellations
          pushed the deal below what was already collected. Company pays this back. */}
      {Number(row.refund_due) > 0 && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
          <FiAlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
          <div className="text-xs text-rose-800">
            <span className="font-bold">Refund {formatPrice(row.refund_due)} due to customer</span> — they
            paid {formatPrice(p?.paid != null ? Number(p.paid) + Number(row.refund_due) : row.refund_due)} but
            the deal is now only {formatPrice(row.total)} after cancellations. Hand back the difference.
          </div>
        </div>
      )}

      {!pending && p.confirmed && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Paid</p>
            <p className="mt-0.5 text-sm font-bold text-slate-800">{formatPrice(p.paid) || '—'}</p>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Balance</p>
            <p className={`mt-0.5 text-sm font-bold ${Number(p.balance_due) > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
              {formatPrice(p.balance_due) || '—'}
            </p>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Recorded</p>
            <p className="mt-0.5 text-sm font-bold text-slate-800">{shortDate(p.recorded_at)}</p>
          </div>
        </div>
      )}

      {!pending && p.type === 'advance' && p.balance_paid && (
        <p className="mt-2 text-xs font-medium text-emerald-600">
          <FiCheckCircle className="mr-1 inline h-3 w-3" />
          Balance collected {shortDate(p.balance_paid_at)}{p.balance_paid_mode_label ? ` via ${p.balance_paid_mode_label}` : ''}
        </p>
      )}
    </div>
  );
};

const PaymentsPage = () => {
  const [rows, setRows] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [toConfirmCount, setToConfirmCount] = useState(0);
  const [needsActionCount, setNeedsActionCount] = useState(0);
  const [balanceDueCount, setBalanceDueCount] = useState(0);
  const [totalBalanceDue, setTotalBalanceDue] = useState('0');
  const [refundDueCount, setRefundDueCount] = useState(0);
  const [totalRefundDue, setTotalRefundDue] = useState('0');
  const [loading, setLoading] = useState(true);
  // Land on everything awaiting the admin — a confirmed deal is ON HOLD (not
  // sold) until the payment is confirmed here.
  const [filter, setFilter] = useState('needs_action');
  const [search, setSearch] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [target, setTarget] = useState(null);
  const [collectTarget, setCollectTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (search.trim()) params.q = search.trim();
      const { data } = await paymentsApi.getWonPayments(params);
      setRows(data?.payments || []);
      setPendingCount(data?.pending_count ?? 0);
      setToConfirmCount(data?.to_confirm_count ?? 0);
      setNeedsActionCount(data?.needs_action_count ?? 0);
      setBalanceDueCount(data?.balance_due_count ?? 0);
      setTotalBalanceDue(data?.total_balance_due ?? '0');
      setRefundDueCount(data?.refund_due_count ?? 0);
      setTotalRefundDue(data?.total_refund_due ?? '0');
    } catch (e) {
      setError(errorMessage(e, 'Failed to load payments'));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  const submitPayment = async (payload) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const wasConfirm = target.payment && !target.payment.confirmed;
      await paymentsApi.setPayment(target.inquiry_id, payload);
      setSuccess(`Payment ${wasConfirm ? 'confirmed' : 'recorded'} for ${target.customer_name || 'the deal'}.`);
      setTarget(null);
      await load();
    } catch (e) {
      setSubmitError(errorMessage(e, 'Failed to save the payment'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitCollect = async (mode) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await paymentsApi.collectBalance(collectTarget.inquiry_id, { paid: true, mode: mode || undefined });
      setSuccess(`Balance collected from ${collectTarget.customer_name || 'the customer'}.`);
      setCollectTarget(null);
      await load();
    } catch (e) {
      setSubmitError(errorMessage(e, 'Failed to mark the balance collected'));
    } finally {
      setSubmitting(false);
    }
  };

  const submitDelete = async ({ reason }) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await paymentsApi.deletePayment(deleteTarget.inquiry_id, { reason });
      setSuccess(`Payment record deleted for ${deleteTarget.customer_name || 'the deal'} — saved in Deleted Records.`);
      setDeleteTarget(null);
      await load();
    } catch (e) {
      setSubmitError(errorMessage(e, 'Failed to delete the payment'));
    } finally {
      setSubmitting(false);
    }
  };

  const recordedCount = rows.filter((r) => r.payment && r.payment.confirmed).length;

  return (
    <MainLayout>
      <div className="space-y-6">
        <PageHeader
          purpose={
            <>
              Confirmed deals are <span className="font-semibold text-amber-600">ON HOLD</span> until you confirm the payment here — only then are they counted as <span className="font-semibold text-emerald-600">SOLD</span>. Sales reports how the customer paid; you check it and hit <span className="font-semibold text-sky-600">Confirm payment</span>.
            </>
          }
        >
          <Button variant="secondary" onClick={load} disabled={loading} className="gap-2">
            <FiRefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </PageHeader>

        {error && <Alert type="error" message={error} onClose={() => setError(null)} />}
        {success && <Alert type="success" message={success} onClose={() => setSuccess(null)} />}

        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon={FiAlertCircle} label="Awaiting confirmation (on hold)" value={needsActionCount} tone="violet" />
          <StatCard icon={FiAlertCircle} label="Sales reported · to confirm" value={toConfirmCount} tone="sky" />
          <StatCard
            icon={FiClock}
            label={`Balance to collect${balanceDueCount ? ` (${balanceDueCount})` : ''}`}
            value={formatPrice(totalBalanceDue) || '₹0'}
            tone="amber"
          />
          <StatCard icon={FiCheckCircle} label="Recorded (sold)" value={recordedCount} tone="emerald" />
          {Number(totalRefundDue) > 0 && (
            <StatCard
              icon={FiAlertCircle}
              label={`Refund due to customers${refundDueCount ? ` (${refundDueCount})` : ''}`}
              value={formatPrice(totalRefundDue) || '₹0'}
              tone="rose"
            />
          )}
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap gap-1.5">
            {FILTERS.map((f) => {
              const badge = {
                needs_action: needsActionCount,
                pending: pendingCount,
                to_confirm: toConfirmCount,
                balance_due: balanceDueCount,
              }[f.value];
              return (
                <button
                  key={f.value}
                  onClick={() => setFilter(f.value)}
                  className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-all ${filter === f.value ? 'bg-violet-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-100'}`}
                >
                  {f.label}
                  {badge > 0 && (
                    <span className={`rounded-full px-1.5 py-0.5 text-[10px] leading-none ${filter === f.value ? 'bg-white/25 text-white' : 'bg-violet-100 text-violet-700'}`}>
                      {badge}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          <div className="relative w-full sm:max-w-sm">
            <FiSearch className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search customer, car, or part…"
              className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm text-slate-800 placeholder-slate-400 shadow-sm transition-all focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-300"
            />
          </div>
        </div>

        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-slate-300 bg-white px-4 py-16 text-center">
            <FiCreditCard className="mb-3 h-8 w-8 text-slate-300" />
            <p className="font-semibold text-slate-700">
              {filter === 'pending' ? 'No pending payments' : search ? 'No matching deals' : 'No won deals yet'}
            </p>
            <p className="mt-1 text-sm text-slate-500">Won deals appear here so you can keep payments up to date.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((row) => (
              <PaymentCard
                key={row.inquiry_id}
                row={row}
                onSet={(r) => { setSubmitError(null); setTarget(r); }}
                onCollect={(r) => { setSubmitError(null); setCollectTarget(r); }}
                onDelete={(r) => { setSubmitError(null); setDeleteTarget(r); }}
              />
            ))}
          </div>
        )}

        {/* The other rail into this checkpoint. Walk-in and admin-direct
            sales are approved here too — the superadmin sees and can act on
            what the admin hasn't got to yet. */}
        <CounterCheckpoint api={counterApi} onChanged={load} />
      </div>

      {target && (
        <SetPaymentModal
          row={target}
          submitting={submitting}
          error={submitError}
          onClose={() => { if (!submitting) { setTarget(null); setSubmitError(null); } }}
          onSubmit={submitPayment}
        />
      )}

      {collectTarget && (
        <CollectBalanceModal
          row={collectTarget}
          submitting={submitting}
          error={submitError}
          onClose={() => { if (!submitting) { setCollectTarget(null); setSubmitError(null); } }}
          onSubmit={submitCollect}
        />
      )}

      {deleteTarget && (
        <DeleteConfirm
          title="Delete this payment record?"
          summary={`${deleteTarget.customer_name || 'Customer'} · ${formatPrice(deleteTarget.payment?.paid ?? deleteTarget.total) || '—'}`}
          consequences={[
            'The recorded payment (mode, amounts, confirmation) is wiped.',
            'The deal drops back to "awaiting payment" — the order itself stays.',
            'Use this only to correct a wrongly-entered payment.',
          ]}
          busy={submitting}
          error={submitError}
          onClose={() => { if (!submitting) { setDeleteTarget(null); setSubmitError(null); } }}
          onConfirm={submitDelete}
        />
      )}
    </MainLayout>
  );
};

export default PaymentsPage;
